import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, RefreshCw, Loader2, AlertTriangle, Ban } from 'lucide-react';

const API_URL = window.location.origin;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_META = {
  active: { label: 'Paying', cls: 'bg-green-100 text-green-700' },
  trialing: { label: 'Trial', cls: 'bg-blue-100 text-blue-700' },
  past_due: { label: 'Past Due', cls: 'bg-amber-100 text-amber-700' },
  unpaid: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
  incomplete: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  incomplete_expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-500' },
  canceled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500' },
  paused: { label: 'Paused', cls: 'bg-gray-100 text-gray-600' }
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'zombies', label: 'Zombies' },
  { key: 'attention', label: 'Needs Attention' },
  { key: 'paying', label: 'Paying' },
  { key: 'trialing', label: 'Trials' },
  { key: 'cancelled', label: 'Cancelled' }
];

const matchesFilter = (s, filter) => {
  if (filter === 'all') return true;
  if (filter === 'zombies') return s.is_zombie;
  if (filter === 'attention') return ['past_due', 'unpaid', 'incomplete'].includes(s.status);
  if (filter === 'paying') return s.status === 'active';
  if (filter === 'trialing') return s.status === 'trialing';
  if (filter === 'cancelled') return ['canceled', 'incomplete_expired'].includes(s.status);
  return true;
};

const SummaryChip = ({ label, value, accent, testId }) => (
  <div className={`rounded-xl border p-4 ${accent}`} data-testid={testId}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-xs mt-0.5">{label}</p>
  </div>
);

export const AdminBillingTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [cancelling, setCancelling] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/billing/subscriptions`, authHeaders());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load Stripe subscriptions');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const handleCancel = async (sub) => {
    if (!window.confirm(`Cancel the ${sub.tier || 'subscription'} for ${sub.customer_email || 'this customer'}?\n\nStripe will stop ALL future charge attempts immediately. This cannot be undone.`)) return;
    setCancelling(sub.id);
    try {
      const res = await axios.post(`${API_URL}/api/admin/billing/subscriptions/${sub.id}/cancel`, {}, authHeaders());
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(null);
    }
  };

  const subs = (data?.subscriptions || [])
    .filter(s => matchesFilter(s, filter))
    .filter(s => !search || (s.customer_email || '').includes(search.toLowerCase()) || (s.customer_name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="admin-billing-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-terracotta" /> Billing
            {data?.stripe_mode && (
              <Badge className={`text-xs ${data.stripe_mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`} data-testid="billing-stripe-mode-badge">
                {data.stripe_mode === 'live' ? 'LIVE MODE' : 'TEST MODE'}
              </Badge>
            )}
          </h2>
          <p className="text-sm text-gray-500">Every Stripe subscription — who's paying, who's failing, and who shouldn't be charged at all.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="billing-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-700" data-testid="billing-error">{error}</CardContent>
        </Card>
      )}

      {data?.stripe_mode === 'test' && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800" data-testid="billing-test-mode-warning">
              <span className="font-semibold">This app is connected to Stripe TEST mode.</span>{' '}
              Real customer subscriptions live in your Stripe LIVE account and will not appear here.
              Update the STRIPE_API_KEY environment variable to your live secret key (sk_live_...) to see real billing data.
            </p>
          </CardContent>
        </Card>
      )}

      {loading && !data ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-sand rounded-xl animate-pulse" />)}</div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <SummaryChip label="Paying" value={data.summary.paying} accent="border-green-200 bg-green-50 text-green-700" testId="billing-summary-paying" />
            <SummaryChip label="Trials" value={data.summary.trialing} accent="border-blue-200 bg-blue-50 text-blue-700" testId="billing-summary-trials" />
            <SummaryChip label="Needs Attention" value={data.summary.needs_attention} accent="border-amber-200 bg-amber-50 text-amber-700" testId="billing-summary-attention" />
            <SummaryChip label="Zombies (no app user)" value={data.summary.zombies} accent="border-red-200 bg-red-50 text-red-700" testId="billing-summary-zombies" />
            <SummaryChip label="Cancelled" value={data.summary.cancelled} accent="border-gray-200 bg-gray-50 text-gray-500" testId="billing-summary-cancelled" />
          </div>

          {data.summary.zombies > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700" data-testid="billing-zombie-warning">
                  <span className="font-semibold">{data.summary.zombies} subscription(s) are still charging customers who no longer exist in the app.</span>{' '}
                  Use the Zombies filter below and cancel each one to stop the charges.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500 hover:border-forest'}`}
                data-testid={`billing-filter-${f.key}`}>
                {f.label}
              </button>
            ))}
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name..." className="max-w-xs ml-auto" data-testid="billing-search-input" />
          </div>

          <div className="space-y-2">
            {subs.map(s => {
              const meta = STATUS_META[s.status] || { label: s.status, cls: 'bg-gray-100 text-gray-600' };
              const cancellable = !['canceled', 'incomplete_expired'].includes(s.status);
              return (
                <Card key={s.id} className={`card-base ${s.is_zombie ? 'border-red-200' : ''}`} data-testid={`billing-sub-${s.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-forest truncate">{s.customer_email || 'no email on Stripe customer'}</p>
                        <Badge className={`${meta.cls} text-xs`}>{meta.label}</Badge>
                        {s.is_zombie && <Badge className="bg-red-100 text-red-700 text-xs" data-testid={`billing-zombie-badge-${s.id}`}>Not in app</Badge>}
                        {s.cancel_at_period_end && cancellable && <Badge variant="outline" className="text-xs">Ends at period end</Badge>}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {s.customer_name ? `${s.customer_name} · ` : ''}{s.tier ? `${s.tier} · ` : ''}
                        ${s.amount}{s.interval ? `/${s.interval}` : ''} {s.currency}
                        {cancellable && s.current_period_end ? ` · next charge ${new Date(s.current_period_end).toLocaleDateString()}` : ''}
                        {s.ended_at ? ` · ended ${new Date(s.ended_at).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                    {cancellable && (
                      <Button variant="outline" size="sm" onClick={() => handleCancel(s)} disabled={cancelling === s.id}
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`billing-cancel-${s.id}`}>
                        {cancelling === s.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />} Cancel Subscription
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {subs.length === 0 && <p className="text-center text-sm text-gray-400 py-6">No subscriptions match this view.</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminBillingTab;
